import {
  Button,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { DatePicker } from "@mui/x-date-pickers";
import { useEffect, useMemo } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useRecoilState, useRecoilValue } from "recoil";
import { useTranslation } from "react-i18next";
import moment from "moment";
import CircleOutlinedIcon from "@mui/icons-material/CircleOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useGetPaymentMethods } from "src/api/generic";
import { useGetEmployeeOutstandingLoans } from "src/api/salary/employees";
import {
  useAddSalaryEventPayment,
  useAddSalaryOwnerPayment,
  useGetSalarySalary,
  usePaySalarySalary,
} from "src/api/salary/salaries";
import { useGetCapitalList } from "src/api/settings/capital";
import { notificationsState } from "src/store/notifications";
import { authState } from "src/store/auth";
import { APIErrorResponse } from "src/types/generic";
import SalaryModalCard from "../salary-modal-card";
import {
  paymentFormDefaultValues,
  paymentFormSchema,
  PaymentFormValues,
} from "./constants";

const SalaryPayModal = ({
  open,
  onClose,
  onPaid,
  salaryId,
  salaryReference,
}: {
  open: boolean;
  onClose: () => void;
  onPaid: () => void;
  salaryId: number;
  salaryReference?: string;
}) => {
  const { t } = useTranslation("salary");
  const { t: generalT } = useTranslation("general");
  const { t: purchasingT } = useTranslation("purchasing");
  const { t: operationCostT } = useTranslation("operationCost");
  const [notifications, setNotifications] = useRecoilState(notificationsState);
  const auth = useRecoilValue(authState);
  const company = auth?.user?.company;

  const { data: salaryRes } = useGetSalarySalary(salaryId, open);
  const detail = salaryRes?.data;
  const employeeId = detail?.employee?.id ? Number(detail.employee.id) : null;
  const { data: pmRes } = useGetPaymentMethods({ page: 1, enabled: open });
  const paymentMethods = pmRes?.data ?? [];
  const { data: ownersData } = useGetCapitalList();
  const { data: loansRes } = useGetEmployeeOutstandingLoans(
    employeeId,
    open && !!employeeId,
  );
  const outstandingLoans = loansRes?.data ?? [];

  const { mutate: payMut, isPending: paying } = usePaySalarySalary();
  const { mutate: ownerMut, isPending: payingOwner } = useAddSalaryOwnerPayment();
  const { mutate: eventMut, isPending: payingEvent } = useAddSalaryEventPayment();

  const remaining = Number(detail?.remaining_amount ?? 0);
  const isPending = paying || payingOwner || payingEvent;

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<PaymentFormValues>({
    mode: "onBlur",
    resolver: yupResolver(paymentFormSchema),
    context: { remaining },
    defaultValues: paymentFormDefaultValues,
  });

  const paymentType = watch("type");
  const selectedEventId = Number(watch("eventId") || 0);

  const selectedLoan = outstandingLoans.find(
    (item: any) => Number(item.id) === selectedEventId,
  );
  const eventCreditRemaining = selectedLoan
    ? Number(selectedLoan.remaining_balance ?? 0)
    : 0;

  useEffect(() => {
    if (!open) return;
    reset({
      ...paymentFormDefaultValues,
      amount: remaining > 0 ? remaining : 0,
      date: new Date(),
    });
  }, [open, remaining, reset]);

  const title = useMemo(() => {
    const ref = salaryReference ?? detail?.reference ?? "";
    return ref ? `${t("paySalary")} - ${ref}` : t("paySalary");
  }, [t, salaryReference, detail?.reference]);

  const pushError = (err?: APIErrorResponse) => {
    setNotifications([
      ...notifications,
      {
        type: "error",
        message: err?.data?.errors?.[0]?.value ?? generalT("somethingWrong"),
      },
    ]);
  };

  const onSubmit: SubmitHandler<PaymentFormValues> = (values) => {
    if (
      !!company?.start_balance_date &&
      values.type !== "event" &&
      values.date &&
      moment(values.date)
        .startOf("day")
        .isBefore(moment(company.start_balance_date).startOf("day"))
    ) {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message: `${generalT("balanceDateValidationMsg")}: ${
            company?.start_balance_date
          }`,
        },
      ]);
      return;
    }

    if (
      values.type === "event" &&
      Number(values.amount) > eventCreditRemaining
    ) {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message: `${operationCostT("eventBalance")}: ${eventCreditRemaining}`,
        },
      ]);
      return;
    }

    const amount = Number(values.amount);
    const onSuccess = () => {
      setNotifications([
        ...notifications,
        { type: "success", message: t("paidSuccessfully") },
      ]);
      onPaid();
    };

    if (values.type === "owner") {
      ownerMut(
        {
          salary_id: salaryId,
          capital_id: Number(values.ownerId),
          amount,
          date: moment(values.date).format("YYYY-MM-DD"),
        },
        { onSuccess, onError: pushError },
      );
      return;
    }

    if (values.type === "event") {
      eventMut(
        {
          salary_id: salaryId,
          purchase_event_id: Number(values.eventId),
          amount,
        },
        { onSuccess, onError: pushError },
      );
      return;
    }

    payMut(
      {
        salaryId,
        payment_method_id: Number(values.payment_method_id),
        amount,
        date: moment(values.date).format("YYYY-MM-DD"),
        note: values.note?.trim() || undefined,
      },
      { onSuccess, onError: pushError },
    );
  };

  return (
    <SalaryModalCard
      open={open}
      onClose={onClose}
      title={title}
      onSubmit={handleSubmit(onSubmit)}
    >
      <Stack spacing={2}>
        <Typography color="#667085" fontSize={14}>
          {t("remainingAmount")}: <b>{remaining.toFixed(2)}</b>
        </Typography>

        <Stack direction="row" alignItems="center" gap={2} flexWrap="wrap">
          <Typography>{purchasingT("paymentType")}</Typography>
          <FormControl size="small">
            <Controller
              name="type"
              control={control}
              render={({ field }) => (
                <RadioGroup {...field} row>
                  {["payment", "event", "owner"].map((item) => (
                    <FormControlLabel
                      key={item}
                      value={item}
                      control={
                        <Radio
                          icon={<CircleOutlinedIcon />}
                          checkedIcon={<CheckCircleIcon />}
                          size="small"
                        />
                      }
                      label={
                        item === "event"
                          ? t("employeeEvent")
                          : purchasingT(item)
                      }
                      sx={{ color: "#98A2B3" }}
                    />
                  ))}
                </RadioGroup>
              )}
            />
          </FormControl>
        </Stack>

        {paymentType === "payment" && (
          <Controller
            name="payment_method_id"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                value={field.value ?? ""}
                size="small"
                select
                label={generalT("paymentMethod")}
                fullWidth
                error={!!errors.payment_method_id}
                helperText={
                  errors.payment_method_id
                    ? t(errors.payment_method_id.message as string)
                    : undefined
                }
              >
                {paymentMethods
                  .filter((pm: any) => pm.status)
                  .map((pm: any) => (
                    <MenuItem key={pm.id} value={pm.id}>
                      {pm.name}
                    </MenuItem>
                  ))}
              </TextField>
            )}
          />
        )}

        {paymentType === "event" && (
          <FormControl fullWidth size="small" error={!!errors.eventId}>
            <InputLabel id="salary-event-label">{purchasingT("event")}</InputLabel>
            <Controller
              name="eventId"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  value={field.value ?? ""}
                  labelId="salary-event-label"
                  label={purchasingT("event")}
                >
                  {outstandingLoans.map((loan: any) => (
                    <MenuItem key={loan.id} value={loan.id}>
                      {t("employeeLoanLabel", {
                        reference: loan.reference,
                        amount: Number(loan.remaining_balance ?? 0).toFixed(2),
                      })}
                    </MenuItem>
                  ))}
                </Select>
              )}
            />
            <FormHelperText>
              {errors.eventId ? t(errors.eventId.message as string) : undefined}
            </FormHelperText>
          </FormControl>
        )}

        {paymentType === "owner" && (
          <FormControl fullWidth size="small" error={!!errors.ownerId}>
            <InputLabel id="salary-owner-label">{purchasingT("owner")}</InputLabel>
            <Controller
              name="ownerId"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  value={field.value ?? ""}
                  labelId="salary-owner-label"
                  label={purchasingT("owner")}
                >
                  {(ownersData?.data ?? []).map((owner: any) => (
                    <MenuItem key={owner.id} value={owner.id}>
                      {owner.name}
                    </MenuItem>
                  ))}
                </Select>
              )}
            />
            <FormHelperText>
              {errors.ownerId ? t(errors.ownerId.message as string) : undefined}
            </FormHelperText>
          </FormControl>
        )}

        <Stack direction="row" gap={1} alignItems="center">
          <Controller
            name="amount"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                size="small"
                type="number"
                label={generalT("amount")}
                fullWidth
                error={!!errors.amount}
                helperText={
                  errors.amount ? t(errors.amount.message as string) : ""
                }
              />
            )}
          />
          {paymentType === "event" && (
            <Typography whiteSpace="nowrap">
              {operationCostT("eventBalance")}: {eventCreditRemaining}
            </Typography>
          )}
        </Stack>

        {paymentType !== "event" && (
          <Controller
            name="date"
            control={control}
            render={({ field }) => (
              <DatePicker
                {...field}
                value={field.value ?? null}
                onChange={(date) => field.onChange(date ?? null)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: "small",
                    error: !!errors.date,
                    helperText: errors.date
                      ? t(errors.date.message as string)
                      : undefined,
                  },
                }}
                format="dd/MM/yyyy"
                label={t("paymentDate")}
              />
            )}
          />
        )}

        {paymentType === "payment" && (
          <Controller
            name="note"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                value={field.value ?? ""}
                size="small"
                label={generalT("notes")}
                fullWidth
                multiline
                minRows={3}
              />
            )}
          />
        )}
      </Stack>

      <Stack spacing={2} direction="row">
        <Button variant="outlined" color="tertiary" fullWidth onClick={onClose}>
          {generalT("cancel")}
        </Button>
        <LoadingButton
          type="submit"
          variant="contained"
          fullWidth
          loading={isPending}
          disabled={remaining <= 0}
        >
          {generalT("pay")}
        </LoadingButton>
      </Stack>
    </SalaryModalCard>
  );
};

export default SalaryPayModal;
